import { Trash2, Plus, Minus, Package } from 'lucide-react'
import useInvoiceStore from '../../stores/useInvoiceStore'

/**
 * Line Item Row Component
 * Editable row for invoice line items
 */
const LineItemRow = ({ item }) => {
  const { updateLineItem, removeLineItem } = useInvoiceStore()

  const handleQuantityChange = (e) => {
    // Allow decimal quantities (e.g., 1.5, 2.25)
    const quantity = parseFloat(e.target.value) || 0
    if (quantity > 0) {
      updateLineItem(item.id, { quantity })
    }
  }

  const incrementQuantity = () => {
    const newQuantity = parseFloat(item.quantity) + 0.1
    updateLineItem(item.id, { quantity: Math.round(newQuantity * 10) / 10 }) // Round to 1 decimal
  }

  const decrementQuantity = () => {
    const newQuantity = parseFloat(item.quantity) - 0.1
    if (newQuantity > 0) {
      updateLineItem(item.id, { quantity: Math.round(newQuantity * 10) / 10 }) // Round to 1 decimal
    }
  }

  const handlePriceChange = (e) => {
    const price = parseFloat(e.target.value) || 0
    if (price >= 0) {
      updateLineItem(item.id, { price })
    }
  }

  const handleRemove = () => {
    removeLineItem(item.id)
  }

  return (
    <div className="flex flex-col sm:grid sm:grid-cols-12 gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      {/* Product Info - Similar to dropdown display */}
      <div className="sm:col-span-12 mb-2 sm:mb-0">
        <div className="flex items-center gap-3">
          {/* Product Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            item.availableQuantity <= 0
              ? 'bg-red-100 dark:bg-red-900/30'
              : item.availableQuantity < 10
              ? 'bg-yellow-100 dark:bg-yellow-900/30'
              : 'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            <Package className={`w-5 h-5 ${
              item.availableQuantity <= 0
                ? 'text-red-600 dark:text-red-400'
                : item.availableQuantity < 10
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-blue-600 dark:text-blue-400'
            }`} />
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 dark:text-white">
              {item.productName}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>{item.category || 'غير مصنف'}</span>
              <span>•</span>
              <span className={`font-semibold ${
                item.availableQuantity <= 0
                  ? 'text-red-600 dark:text-red-400'
                  : item.availableQuantity < 10
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-green-600 dark:text-green-400'
              }`}>
                متوفر في السيارة: {item.availableQuantity || 0} {item.unit || 'وحدة'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quantity Input with +/- buttons - Mobile friendly */}
      <div className="sm:col-span-4">
        <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block sm:hidden">الكمية</label>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={decrementQuantity}
            className="p-1.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex-shrink-0"
            aria-label="تقليل الكمية"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={item.quantity}
            onChange={handleQuantityChange}
            className="flex-1 min-w-0 px-2 py-1.5 text-sm text-center font-semibold rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="button"
            onClick={incrementQuantity}
            className="p-1.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex-shrink-0"
            aria-label="زيادة الكمية"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Price Input */}
      <div className="sm:col-span-3">
        <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block sm:hidden">السعر</label>
        <input
          type="number"
          min="0"
          step="0.001"
          value={item.price}
          onChange={handlePriceChange}
          className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Total */}
      <div className="sm:col-span-4 flex items-center justify-between sm:justify-start">
        <span className="text-xs text-gray-600 dark:text-gray-400 sm:hidden">الإجمالي:</span>
        <p className="font-semibold text-gray-900 dark:text-white">
          {item.total.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
        </p>
      </div>

      {/* Delete Button */}
      <div className="sm:col-span-1 flex justify-end sm:justify-center">
        <button
          onClick={handleRemove}
          className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
          aria-label="حذف"
        >
          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
        </button>
      </div>
    </div>
  )
}

export default LineItemRow
